import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { AdmonitionNodeView } from './admonition-node-view'
import { nanoid } from 'nanoid';
import { IconCategory, IconType } from '@deepguide-ai/types';
export type AdmonitionType = 'note' | 'tip' | 'info' | 'warning' | 'danger'


export interface AdmonitionAttributes {
  id?: string
  admonitionType?: AdmonitionType
  title?: string
  showTitle?: boolean
  icon?: string
  iconCategory?: IconCategory
  iconType?: IconType
  iconColor?: string
  showIcon?: boolean
  useCustomIcon?: boolean
}

export const AdmonitionSpec = {
  group: 'block',
  content: 'block*',
  attrs: {
    type: { default: 'note' },
    title: { default: null },
    showTitle: { default: true },
    icon: { default: null },
    iconType: { default: 'REGULAR' },
    iconColor: { default: '#3b82f6' },
    showIcon: { default: true },
    useCustomIcon: { default: false },
    id: { default: nanoid(10) },
  },
  toDOM() { return ['div', { 'data-admonition': '' }, 0] as [string, Record<string, any>, 0]; },
  parseDOM: [{ tag: 'div[data-admonition]' }],
};


declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    admonition: {
      /**
       * Insert an admonition
       */
      insertAdmonition: (attributes?: AdmonitionAttributes) => ReturnType
      /**
       * Update admonition attributes
       */
      updateAdmonition: (attributes: Partial<AdmonitionAttributes>) => ReturnType
    }
  }
}

export const AdmonitionExtension = Node.create({
  name: 'Admonition',

  group: 'block',

  content: 'block+',

  isolating: true,

  defining: true,

  addAttributes() {
    return {
      id: {
        default: nanoid(10),
        parseHTML: element => element.getAttribute('data-dg-id'),
        renderHTML: attributes => {
          if (!attributes.id) {
            return {}
          }
          return {
            'data-dg-id': attributes.id,
          }
        },
      },
      admonitionType: {
        default: 'note',
        parseHTML: element => element.getAttribute('data-admonition-type') || 'note',
        renderHTML: attributes => {
          if (!attributes.admonitionType) {
            return {}
          }
          return {
            'data-admonition-type': attributes.admonitionType,
          }
        },
      },
      title: {
        default: '',
        parseHTML: element => element.getAttribute('data-title'),
        renderHTML: attributes => {
          if (!attributes.title) {
            return {}
          }
          return {
            'data-title': attributes.title,
          }
        },
      },
      showTitle: {
        default: true,
        parseHTML: element => element.getAttribute('data-show-title') !== 'false',
        renderHTML: attributes => {
          if (attributes.showTitle === undefined) {
            return {}
          }
          return {
            'data-show-title': attributes.showTitle,
          }
        },
      },
      icon: {
        default: 'sparkles',
        parseHTML: element => element.getAttribute('data-icon'),
        renderHTML: attributes => {
          if (!attributes.icon) {
            return {}
          }
          return {
            'data-icon': attributes.icon,
          }
        },
      },
      iconType: {
        default: 'REGULAR',
        parseHTML: element => element.getAttribute('data-icon-type'),
        renderHTML: attributes => {
          if (!attributes.iconType) {
            return {}
          }
          return {
            'data-icon-type': attributes.iconType,
          }
        },
      },
      iconColor: {
        default: '#3b82f6',
        parseHTML: element => element.getAttribute('data-icon-color'),
        renderHTML: attributes => {
          if (!attributes.iconColor) {
            return {}
          }
          return {
            'data-icon-color': attributes.iconColor,
          }
        },
      },
      showIcon: {
        default: true,
        parseHTML: element => element.getAttribute('data-show-icon') !== 'false',
        renderHTML: attributes => {
          if (attributes.showIcon === undefined) {
            return {}
          }
          return {
            'data-show-icon': attributes.showIcon,
          }
        },
      },
      useCustomIcon: {
        default: false,
        parseHTML: element => element.getAttribute('data-use-custom-icon') === 'true',
        renderHTML: attributes => {
          if (attributes.useCustomIcon === undefined) {
            return {}
          }
          return {
            'data-use-custom-icon': attributes.useCustomIcon,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="Admonition"]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'Admonition' }),
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(AdmonitionNodeView)
  },

  addCommands() {
    return {
      insertAdmonition:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              id: nanoid(10),
              admonitionType: 'note',
              title: '',
              showTitle: true,
              icon: 'sparkles',
              iconType: 'REGULAR',
              iconColor: '#3b82f6',
              showIcon: true,
              useCustomIcon: false,
              ...attributes,
            },
            content: [
              {
                type: 'paragraph',
                content: [
                  
                ]
              }
            ],
          })
        },
      updateAdmonition:
        (attributes) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      // Prevent deleting the admonition with backspace when cursor is at the beginning
      Backspace: ({ editor }) => {
        const { selection, doc } = editor.state
        const { $from } = selection

        // Check if we're at the beginning of an admonition
        if ($from.pos === 1) return false

        const node = $from.node()
        if (node.type.name === this.name && $from.parentOffset === 0) {
          return true // Prevent deletion
        }

        return false
      },
    }
  },
}) 